import { FieldCellValueUI, FormFieldValueGroupUI, UNGROUPED_CELL_ID } from "Backend";
import { FieldValueGroupSelectionStatus } from "generated/graphql";
import displayCellValue from "./displayCellValue";
import { useCallback, useContext, useMemo, useState } from "react";
import { FormFieldContext, GroupedValueGroup } from "contexts/FormFieldContext";
import SourceTypeIcon from "./SourceTypeIcon";
import { sortValueGroups } from "./sortValueGroups";
import { useFilteredValueGroups } from "helpers/hooks/useFilteredValueGroups";
import { TagDisplay } from "components/BetterResponseTable/TagDisplay";
import { cn } from "components/lib/utils";
import { Bookmark, BookmarkBorder, Bookmarks, Star } from "@mui/icons-material";

type GroupedValueGroupWithPossiblyUndefinedItems = Omit<GroupedValueGroup, "valueGroups"> & {
  valueGroups: (FormFieldValueGroupUI | undefined)[];
};
function responseMenuItem({
  active,
  group,
  setGroupId,
  vgDate,
  vgLensSubtype,
  vgName,
  vgRecordType,
}: {
  active: boolean;
  group: FormFieldValueGroupUI | undefined;
  setGroupId: (value: { groupId?: number; groupName?: string }) => void;
  vgDate?: FieldCellValueUI;
  vgLensSubtype?: FieldCellValueUI;
  vgName?: FieldCellValueUI;
  vgRecordType?: FieldCellValueUI;
  groupName?: string;
}) {
  const scrollRef = useCallback(
    (node: HTMLDivElement) => {
      // TODO: debounce?
      if (node !== null && active) node.scrollIntoView({ behavior: "smooth", block: "center" });
    },
    [active]
  );
  // this should trigger when the response is deleted,
  // normally would return earlier but we need to maintain the same number of hooks
  // the scroll hook is useful enough this seems worth it.
  if (group === undefined) return null;
  const referenceCount = group.references.length;

  return (
    <div
      key={group.id}
      ref={scrollRef}
      className={cn(
        active ? "bg-peri-100 hover:bg-peri-200" : "hover:bg-peri-50",
        "cursor-pointer max-w-64 p-2 overflow-x-hidden"
      )}
      onClick={() => setGroupId({ groupId: group.id })}>
      <div className="flex items-center">
        <p className="text-xs font-semibold pr-0.5">#{group.responseNumber}</p>
        {group.selectionStatus === FieldValueGroupSelectionStatus.Selected && <Star className="h-4 w-4 text-gold" />}
        {group.tags.length > 0 && (
          <TagDisplay.Dense
            readOnly
            availableTags={group.availableTags}
            appliedTags={group.tags}
            valueGroupSerialId={group.serialId}
          />
        )}
        {group.source && <SourceTypeIcon className="ml-1 text-gray-400 h-4 w-4" valueGroupSource={group.source} />}
        <div className="grow" />
        {referenceCount > 0 ? (
          <span className="text-xxs inline-flex items-center gap-0.5">
            {group.references.length}
            {group.references.length > 1 ? (
              <Bookmarks className="h-3 w-3 text-gray-400" />
            ) : (
              <Bookmark className="h-3 w-3 text-gray-400" />
            )}
          </span>
        ) : (
          <span className="text-xxs inline-flex items-center gap-0.5">
            <BookmarkBorder className="h-3 w-3 text-gray-400" />
          </span>
        )}
      </div>
      <div className="flex flex-col gap-1 mt-1">
        {vgDate?.value.dateValue && (
          <p className="text-xxs flex flex-col">
            <span>{displayCellValue(vgDate?.value)}</span>
          </p>
        )}
        {vgLensSubtype?.value && (
          <p className="text-xxs flex flex-col">
            <span>{displayCellValue(vgLensSubtype.value)}</span>
          </p>
        )}
        {vgName?.value.textValue && (
          <p className="text-xxs flex flex-col">
            <span>{displayCellValue(vgName?.value)}</span>
          </p>
        )}
        {vgRecordType?.value.textValue && (
          <p className="text-xxs flex flex-col">
            <span>{displayCellValue(vgRecordType?.value)}</span>
          </p>
        )}
      </div>
    </div>
  );
}

export const ValueGroupNavigationPanel = (props: {
  groupId: number;
  groupResponseNumber: number;
  setGroupId: (value: { groupId?: number; groupName?: string }) => void;
  valueGroups: FormFieldValueGroupUI[];
}) => {
  const ffContext = useContext(FormFieldContext);

  const ungrouped = ffContext.groupByCellIdHandle[0] === UNGROUPED_CELL_ID;
  const { dateCellIndex, lensSubtypeCellIndex, nameCellIndex, recordTypeCellIndex } = ffContext;
  const { groupId, setGroupId, valueGroups } = props;
  const { groupedAndFilteredValueGroupsWithReferences } = useFilteredValueGroups();
  const [initialValueGroups] = useState(valueGroups);
  const [initialGrouped] = useState(groupedAndFilteredValueGroupsWithReferences);

  // If a tag gets removed such that the group should no longer be in the queue, we still want to show it
  // so keep this copy based on the initial queue and look up the current state in ffContext
  // This way we still see updates in the panel
  const updateFromContextIfNeeded = (initialVg: FormFieldValueGroupUI): FormFieldValueGroupUI | undefined =>
    ffContext.valueGroupById(initialVg.id);
  const vgCopy = useMemo(() => {
    return initialValueGroups.map(updateFromContextIfNeeded);
  }, [valueGroups]);
  const groupedCopy = useMemo(() => {
    return initialGrouped.map((initialGroup) => ({
      ...initialGroup,
      valueGroups: initialGroup.valueGroups.map(updateFromContextIfNeeded),
    }));
  }, [groupedAndFilteredValueGroupsWithReferences]);

  const groupedMenuItems = ({ groups }: { groups: GroupedValueGroupWithPossiblyUndefinedItems[] }) => {
    return groups
      .map((group, index) => {
        const groupSort = ffContext.fieldUIState?.groupSorts[group.cellValue];
        const menuHeader = group.cellValue || `No ${ffContext.groupByCell?.name ?? "Data"}`;
        const sortedValueGroups =
          groupSort !== undefined
            ? sortValueGroups(group.valueGroups, groupSort.sortDir, groupSort.cell)
            : group.valueGroups;

        if (sortedValueGroups.length === 0) return null;

        return (
          <>
            <h1
              className="font-semibold text-gray-500 text-sm py-2 text-wrap break-all max-w-64"
              key={`header-${index}`}>
              {menuHeader}
            </h1>
            {sortedValueGroups.map((valueGroup: FormFieldValueGroupUI | undefined) => {
              if (valueGroup === undefined) {
                return responseMenuItem({
                  active: false,
                  group: valueGroup,
                  setGroupId,
                  vgDate: undefined,
                  vgLensSubtype: undefined,
                  vgName: undefined,
                  vgRecordType: undefined,
                });
              }
              const vgDate = ffContext.dateCellIndex !== -1 ? valueGroup.values[ffContext.dateCellIndex] : undefined;
              const vgLensSubtype =
                ffContext.lensSubtypeCellIndex !== -1 ? valueGroup.values[ffContext.lensSubtypeCellIndex] : undefined;
              const vgName = ffContext.nameCellIndex !== -1 ? valueGroup.values[ffContext.nameCellIndex] : undefined;
              const vgRecordType =
                ffContext.recordTypeCellIndex !== -1 ? valueGroup.values[ffContext.recordTypeCellIndex] : undefined;
              return responseMenuItem({
                active: valueGroup.id === groupId,
                group: valueGroup,
                setGroupId,
                vgDate,
                vgLensSubtype,
                vgName,
                vgRecordType,
                groupName: group.cellValue,
              });
            })}
          </>
        );
      })
      .flat();
  };

  const menuItems = (valueGroups: (FormFieldValueGroupUI | undefined)[]) =>
    valueGroups.length > 0 &&
    valueGroups.map((existingVG) => {
      if (existingVG === undefined) {
        return responseMenuItem({
          active: false,
          group: existingVG,
          setGroupId,
          vgDate: undefined,
          vgLensSubtype: undefined,
          vgName: undefined,
          vgRecordType: undefined,
        });
      }

      const vgDate = dateCellIndex !== -1 ? existingVG.values[dateCellIndex] : undefined;
      const vgLensSubtype = lensSubtypeCellIndex !== -1 ? existingVG.values[lensSubtypeCellIndex] : undefined;
      const vgName = nameCellIndex !== -1 ? existingVG.values[nameCellIndex] : undefined;
      const vgRecordType = recordTypeCellIndex !== -1 ? existingVG.values[recordTypeCellIndex] : undefined;
      return responseMenuItem({
        active: existingVG.id === groupId,
        group: existingVG,
        setGroupId,
        vgDate,
        vgLensSubtype,
        vgName,
        vgRecordType,
      });
    });

  const navItems = ungrouped ? menuItems(vgCopy) : groupedMenuItems({ groups: groupedCopy });
  return <div className="divide-y divide-gray-100">{navItems}</div>;
};
